import { WebTracerProvider } from '@opentelemetry/sdk-trace-web'
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http'
import { ReadableSpan, Span, SpanProcessor } from '@opentelemetry/sdk-trace-base'
import { BatchSpanProcessor } from '@opentelemetry/sdk-trace-base/build/src/platform/browser'
import { Resource } from '@opentelemetry/resources'
import { SemanticResourceAttributes } from '@opentelemetry/semantic-conventions'
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch'
import { registerInstrumentations } from '@opentelemetry/instrumentation'
import { Context } from '@opentelemetry/api'
import { getOrCreateBrowserSessionId, hashSessionId } from './session'

type TracingAttributes = {
  'kn.client.platform': 'web'
  'kn.client.app': 'solvay-react'
  'kn.session.id_hash': string
}

type TracingContext = {
  attributes: TracingAttributes
}

let tracingContextPromise: Promise<TracingContext> | null = null
let tracerProvider: WebTracerProvider | null = null

const createHexId = (size: number): string => {
  const bytes = new Uint8Array(size / 2)
  window.crypto.getRandomValues(bytes)

  return Array.from(bytes)
    .map((value) => value.toString(16).padStart(2, '0'))
    .join('')
}

class SessionSpanProcessor implements SpanProcessor {
  private sessionIdHash: string | null = null

  constructor() {
    void this.initSessionHash()
  }

  private async initSessionHash(): Promise<void> {
    const sessionId = getOrCreateBrowserSessionId()
    this.sessionIdHash = await hashSessionId(sessionId)
  }

  onStart(span: Span, _parentContext: Context): void {
    if (this.sessionIdHash) {
      span.setAttribute('kn.session.id_hash', this.sessionIdHash)
    }
  }

  onEnd(_span: ReadableSpan): void {}

  shutdown(): Promise<void> {
    return Promise.resolve()
  }

  forceFlush(): Promise<void> {
    return Promise.resolve()
  }
}

export const getTracingContext = async (): Promise<TracingContext> => {
  if (!tracingContextPromise) {
    tracingContextPromise = hashSessionId(getOrCreateBrowserSessionId()).then((sessionIdHash) => ({
      attributes: {
        'kn.client.platform': 'web',
        'kn.client.app': 'solvay-react',
        'kn.session.id_hash': sessionIdHash,
      },
    }))
  }

  return tracingContextPromise
}

export const initializeTracing = async (): Promise<void> => {
  if (tracerProvider) {
    return
  }

  await getTracingContext()

  const resource = new Resource({
    [SemanticResourceAttributes.SERVICE_NAME]: 'solvay-react',
    'kn.client.platform': 'web',
    'kn.client.app': 'solvay-react',
  })

  tracerProvider = new WebTracerProvider({ resource })

  const otlpEndpoint = process.env.REACT_APP_OTLP_ENDPOINT || 'http://localhost:4318/v1/traces'
  const exporter = new OTLPTraceExporter({ url: otlpEndpoint })

  tracerProvider.addSpanProcessor(new BatchSpanProcessor(exporter))
  tracerProvider.addSpanProcessor(new SessionSpanProcessor())

  tracerProvider.register()

  registerInstrumentations({
    instrumentations: [
      new FetchInstrumentation({
        propagateTraceHeaderCorsUrls: /.*/,
        clearTimingResources: true,
      }),
    ],
  })
}

export const buildRequestTracingHeaders = async (): Promise<{ traceparent: string }> => {
  await getTracingContext()

  if (!tracerProvider) {
    await initializeTracing()
  }

  return {
    traceparent: `00-${createHexId(32)}-${createHexId(16)}-01`,
  }
}

export const resetTracingForTests = () => {
  tracingContextPromise = null
  tracerProvider = null
}
